import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Check, ArrowLeft, ArrowRight, Save, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import StepFamily from "@/pages/wizard/StepFamily";
import StepIncome from "@/pages/wizard/StepIncome";
import StepExpenses from "@/pages/wizard/StepExpenses";
import StepReview from "@/pages/wizard/StepReview";
import StepSubmit from "@/pages/wizard/StepSubmit";

const STEPS = ["Grupo familiar", "Ingresos", "Gastos extraordinarios", "Revisión", "Envío"];

export default function Wizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [app, setApp] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const pending = useRef({});
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/applications/${id}`);
      if (data.status !== "draft") { navigate(`/solicitud/${id}/resultado`); return; }
      setApp(data);
      setStep(data.current_step || 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/panel");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const flush = useCallback(async () => {
    if (Object.keys(pending.current).length === 0) return;
    const fields = { ...pending.current };
    pending.current = {};
    setSaving(true);
    try {
      await api.put(`/applications/${id}/step`, fields);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }, [id]);

  const patch = useCallback((fields) => {
    setApp((prev) => ({ ...prev, ...fields }));
    pending.current = { ...pending.current, ...fields };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 700);
  }, [flush]);

  // when documents change, DocumentUpload returns the full updated app
  const applyServerApp = (serverApp) => setApp((prev) => ({ ...prev, ...serverApp }));

  const goStep = async (n) => {
    await flush();
    setStep(n);
    patch({ current_step: n });
  };

  const saveLater = async () => {
    await flush();
    toast.success("Progreso guardado. Puedes continuar más tarde.");
    navigate("/panel");
  };

  if (!app) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const progress = (step / STEPS.length) * 100;
  const stepProps = { app, patch, docs: app.documents || [], applyServerApp, reload: load };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-border sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-primary" />
            <span className="font-heading font-extrabold text-lg text-primary">EIM Becas</span>
          </div>
          <div className="flex items-center gap-2">
            {saving && <span className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</span>}
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-5 pb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-primary" data-testid="wizard-step-label">Paso {step} de {STEPS.length}: {STEPS[step - 1]}</p>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-accent transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <div className="hidden sm:flex justify-between mt-3">
            {STEPS.map((s, i) => (
              <button key={s} onClick={() => goStep(i + 1)}
                className={`flex items-center gap-1 text-xs font-medium ${i + 1 <= step ? "text-primary" : "text-muted-foreground"}`}>
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i + 1 < step ? "bg-accent text-white" : i + 1 === step ? "bg-primary text-white" : "bg-secondary"}`}>
                  {i + 1 < step ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                {s}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8 fade-in">
        {step === 1 && <StepFamily {...stepProps} />}
        {step === 2 && <StepIncome {...stepProps} />}
        {step === 3 && <StepExpenses {...stepProps} />}
        {step === 4 && <StepReview {...stepProps} goStep={goStep} />}
        {step === 5 && <StepSubmit {...stepProps} goStep={goStep} onSubmitted={() => navigate(`/solicitud/${id}/resultado`)} flush={flush} />}

        <div className="flex flex-wrap items-center justify-between gap-3 mt-10">
          <Button variant="ghost" onClick={() => goStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="btn-prev-step">
            <ArrowLeft className="w-4 h-4 mr-1" /> Atrás
          </Button>
          <div className="flex flex-wrap gap-3 justify-end">
            <Button variant="outline" onClick={saveLater} className="rounded-xl border-2" data-testid="btn-save-later">
              <Save className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Guardar y continuar después</span><span className="sm:hidden">Guardar</span>
            </Button>
            {step < 5 && (
              <Button onClick={() => goStep(step + 1)} className="btn-lift rounded-xl h-11 px-6" data-testid="btn-next-step">
                Continuar <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
